// File: client/src/pages/story-workspace.tsx

import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo, useRef, Dispatch, SetStateAction } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Header } from "@/components/Header";
import { Home, BookOpen, Settings, Users, BarChart3, Lightbulb, FileText, Layers, BookText, Palette, Package } from "lucide-react";
import WriteStudio from "@/writer/WriteStudio";
import { MainView, ProjectState, Chapter, Project, OptimizedDataLoading } from "@/writer/types";
import { AppContext, AppContextType } from "@/writer/contexts/AppContext";
import { useToast } from "@/hooks/use-toast";
import { initialAuthorDetails, initialBackMatter, initialBookMetadata, initialFrontMatter, initialPublishingEssentials, initialWorldBuildingData, initialProjectState, initialChapterVitals } from "@/writer/constants";
import { Editor } from "@tiptap/react";
import { cn } from "@/lib/utils";
import { useChapterLazyLoading } from "@/writer/hooks/useChapterLazyLoading";
import { cacheManager } from "@/utils/cacheUtils";
import { deriveChaptersState, validateChapterStateChange, logChapterStateTransition } from "@/utils/chapterStateUtils";
import { readProjectCachesSync } from "@/utils/cacheBootstrap";
import { computeHasChapters, computeChaptersCount, validateChapterConsistency } from "@/utils/hasChaptersCanonical";
import { apiService } from "@/services/api";
import { getChaptersWithErrorHandling } from "@/services/chaptersApi";
import { useOptimizedStoryData } from "@/hooks/useOptimizedStoryData";
import { WorldBuildingData, DetailedCharacter, LocationProfile, CustomWorldBuildingCategory } from "@/writer/types";

import { safeProjectTitle } from "@/utils/safeProjectAccess";

// REMOVED: transformBlueprintToWorldBuildingData function - no longer needed with single source of truth approach

// Helper functions for project info display
function getProjectType(project: any) {
  return project?.bookType || project?.projectData?.bookType || 'Novel';
}

function getProjectWordCount(project: any) {
  return project?.targetWordCount || project?.projectData?.targetWordCount || 0;
}

// Project interface moved to @/writer/types to prevent circular imports

export default function StoryWorkspacePage() {
  // 🔇 DEDUPLICATED LOGGING: Use ref to track render count and avoid spam
  const renderCountRef = useRef(0);
  const renderTimestampRef = useRef(Date.now());
  renderCountRef.current++;
  const shouldLogTrace = renderCountRef.current === 1; // Only log first render

  // Initialize any potentially problematic variables early with defensive checks
  const params = useParams();
  const projectId = params?.id; // Add optional chaining

  // 🔇 Project ID logging (disabled to prevent spam)

  const navigate = useNavigate();
  const { search } = useLocation();
  const { toast } = useToast();
  const chapterLazyLoading = useChapterLazyLoading();

  // 🔧 FIX: ALL HOOKS MUST BE DECLARED FIRST to prevent React error #310
  // Initialize mainView from URL immediately to prevent race condition with API queries
  const urlTab = new URLSearchParams(search).get("tab") as MainView;
  const initialMainView = urlTab || 'ideaHub';

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [projectState, setProjectState] = useState<ProjectState | null>(null);
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});
  const setError = (msg: string) => toast({ title: "Error", description: msg, variant: "destructive" });
  const [mainView, setMainView] = useState<MainView>(initialMainView);
  const [activeChapter, setActiveChapter] = useState<Chapter | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [chapterUpdateTrigger, setChapterUpdateTrigger] = useState(0); // Force re-render trigger

  // 🚀 STABLE MOUNT TRACKING: Use useRef to avoid causing re-renders
  const mountCountRef = useRef({ count: 0, lastMount: Date.now() });
  // 🚀 STABLE URL SYNC: Only update when URL actually changes
  const prevSearchRef = useRef(search);
  // 🔄 STABLE CHAPTER UPDATE TRACKING: Only log meaningful changes
  const prevChapterTriggerRef = useRef(chapterUpdateTrigger);

  // 🚀 TRUE HYDRATION BARRIER: Don't render until caches are normalized
  const [hydrated, setHydrated] = useState(false);
  const [normalizedCacheData, setNormalizedCacheData] = useState<any>(null);

  // ✅ FIXED: No more proactive cache invalidation to prevent redundant API calls
  // The blueprint generation API now includes all necessary data in the response
  const queryClient = useQueryClient();

  // 🚀 OPTIMIZED API LOADING - Intelligent per-tab loading strategy (with re-render trigger)
  const optimizedData = useOptimizedStoryData(projectId, mainView);
  const { project, blueprint, chapters: chaptersFromDb, isLoading: optimizedLoading, errors } = optimizedData;

  // Extract blueprint state for easier access (needed before useEffect)
  const { blueprintExists, blueprintLoaded, blueprintData, blueprintUpdated, blueprintSaving, blueprintError } = blueprint || {};

  // 🔒 STABLE BLUEPRINT REFERENCE: Memoize to prevent unnecessary re-renders (needed before useEffect)
  const stableBlueprintContent = useMemo(() => {
    return blueprintData?.blueprint;
  }, [blueprintData?.blueprint]);

  // 🔒 INITIALIZATION TRACKER: Use ref to prevent circular dependency
  const initializationRef = useRef<{
    projectId: string | null;
    blueprintHash: string | null;
  }>({ projectId: null, blueprintHash: null });

  useEffect(() => {
    // 🔧 FIX: Only require project to initialize — blueprint data merges in when available
    if (!project) {
      return;
    }

    // 🔒 STABLE HASH: Create hash from project + optional blueprint to detect real changes
    const blueprintHash = JSON.stringify({
      uuid: project.uuid,
      id: project.id,
      hasBlueprint: blueprint?.blueprintExists ?? false,
      bookMetadataTitle: stableBlueprintContent?.bookMetadata?.title ?? null,
      chaptersCount: chaptersFromDb?.length || 0
    });

    // 🔒 GUARD: Skip if already initialized with the same data
    if (initializationRef.current.projectId === project.uuid && 
        initializationRef.current.blueprintHash === blueprintHash) {
      return; // Already initialized with same data, skip update
    }

    const newProjectState: ProjectState = {
      ...(initialProjectState as ProjectState),
      projectId: project.uuid,
      currentProjectId: project.id,
      currentProjectName: project.title,
      bookMetadata: stableBlueprintContent?.bookMetadata || initialBookMetadata,
      chapters: chaptersFromDb || [],
      hasInitialBlueprint: blueprint?.blueprintExists ?? false,
      blueprint: {
        ...initialProjectState.blueprint,
        ...(stableBlueprintContent || {}),
      },
    };
    
    // 🔇 Only log on first initialization
    if (initializationRef.current.projectId !== project.uuid && process.env.NODE_ENV === 'development') {
    }
    
    // 🔒 UPDATE TRACKER: Mark as initialized
    initializationRef.current = {
      projectId: project.uuid,
      blueprintHash
    };
    
    setProjectState(newProjectState);
  }, [project?.uuid, project?.id, stableBlueprintContent, blueprint?.blueprintExists, chaptersFromDb]);

  // Blueprint readiness check (uses API data directly)
  const isBlueprintReady = useMemo(() => {
    return !!(blueprint && (blueprint.blueprintExists || blueprint.blueprintLoaded));
  }, [blueprint?.blueprintExists, blueprint?.blueprintLoaded]);

  // 🔒 SECURITY: Compute effective project ID (UUID-first) for all operations
  const isNumericId = projectId ? !isNaN(Number(projectId)) : false;
  const effectiveProjectId = project?.uuid || projectId; // Always prefer UUID when available
  const shouldBlockApiCalls = isNumericId && !project?.uuid; // Block API calls for numeric IDs until UUID loads

  // Stable navigation function to prevent useEffect dependency issues
  const navigateToTab = useCallback((view: MainView, replace = true) => {
    const idToUse = effectiveProjectId || projectId; // Use UUID when available for navigation
    navigate(`/story-workspace/${idToUse}?tab=${view}`, { replace });
  }, [navigate, effectiveProjectId, projectId]);

  // ✅ STABLE REFERENCES: Memoize nested properties for hasBlueprintExists to prevent unnecessary re-renders
  const projectHasBlueprint = useMemo(() => (project as any)?.hasBlueprint, [(project as any)?.hasBlueprint]);
  const blueprintDataBlueprint = useMemo(() => blueprint?.blueprintData?.blueprint, [blueprint?.blueprintData?.blueprint]);
  const chaptersFromDbLength = useMemo(() => chaptersFromDb?.length || 0, [chaptersFromDb?.length]);
  const projectDataHasInitialBlueprint = useMemo(() => project?.projectData?.hasInitialBlueprint, [project?.projectData?.hasInitialBlueprint]);
  const projectUuid = useMemo(() => project?.uuid, [project?.uuid]);
  const projectId_stable = useMemo(() => project?.id, [project?.id]);
  const projectTitle_stable = useMemo(() => project?.title, [project?.title]);

  // Memoized blueprint existence check to prevent circular updates
  const hasBlueprintExists = useMemo(() => {
    if (!project) return false;

    const result = projectHasBlueprint === true || // Priority: Summary endpoint hasBlueprint flag
           blueprintExists === true || // API call to blueprint endpoint
           !!blueprintDataBlueprint || // If we fetched the full blueprint, it exists
           (chaptersFromDbLength > 0) || // If chapters exist, blueprint must exist
           (projectDataHasInitialBlueprint === true); // Fallback to persisted flag

    // 🔇 Blueprint existence decision logging (disabled to prevent spam)
    // Uncomment for debugging if needed
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('🔍 BLUEPRINT EXISTS DECISION for:', projectUuid ?? '(pending)', {
    //     finalDecision: result
    //   });
    // }

    return result;
  }, [blueprintExists, blueprintDataBlueprint, chaptersFromDbLength, projectDataHasInitialBlueprint, projectHasBlueprint, projectUuid, projectId_stable, projectTitle_stable, project?.projectData]);

  // Memoized chapter mapping to prevent unnecessary re-computations
  const mappedChapters = useMemo(() => {
    if (!project || !projectId) return [];

    return (chaptersFromDb || project.projectData?.chapters || []).map((ch: any) => ({
      id: ch.id,
      projectId: ch.projectId || ch.project_id || parseInt(projectId || '0', 10),
      title: ch.title || '',
      summary: ch.summary || '',
      content: ch.content || '',
      order: ch.order || ch.chapter_order || 0,
      plotOutline: ch.plotOutline || '',
      geography: ch.geography || '',
      charactersInvolved: ch.charactersInvolved || '',
      characterActions: ch.characterActions || '',
      soulOfChapter: ch.soulOfChapter || '',
      expectedWordCount: ch.expectedWordCount || ch.expected_word_count || 0,
      status: ch.status || 'draft',
      history: Array.isArray(ch.history) ? ch.history : [],
      historyIndex: typeof ch.historyIndex === 'number' ? ch.historyIndex : -1,
      vitals: { ...initialChapterVitals, ...(ch.vitals || {}) },
      previewContent: ch.previewContent || ch.preview_content || null,
      previewSummary: ch.previewSummary || ch.preview_summary || null,
      previewWordCountTarget: ch.previewWordCountTarget || ch.preview_word_count_target || 0,
      createdAt: ch.createdAt || ch.created_at || new Date(),
      updatedAt: ch.updatedAt || ch.updated_at || new Date(),
    }));
  }, [chaptersFromDb, project?.projectData?.chapters, projectId]);

  // ✅ CHAPTER SOURCE FREEZING: Prevent oscillation by freezing chosen source until clearly newer
  const chosenSourceRef = useRef<{key: string; updatedAt?: string} | null>(null);

  // Stable references for chapter state derivation
  const projectChapterCount = useMemo(() => project?.chapterCount, [project?.chapterCount]);
  const projectUpdatedAt = useMemo(() => project?.updatedAt, [project?.updatedAt]);
  const projectTitle = useMemo(() => project?.title, [project?.title]);
  const projectStateChaptersLength = useMemo(() => projectState?.chapters?.length, [projectState?.chapters?.length]);
  const projectStateBookMetadataTitle = useMemo(() => projectState?.bookMetadata?.title, [projectState?.bookMetadata?.title]);

  // 🚀 DETERMINISTIC CHAPTER STATE: Use deriveChaptersState with normalized cache data
  const chapterStateResult = useMemo(() => {
    try {
      const timestamps = {
        preview: chaptersFromDb?.[0]?.updatedAt ||
                (chaptersFromDb && chaptersFromDb.length > 0 ? new Date().toISOString() : undefined),
        fromDb: chaptersFromDb?.[0]?.updatedAt ||
               (chaptersFromDb && chaptersFromDb.length > 0 ? new Date().toISOString() : undefined),
        bootstrap: undefined,
        projectSummary: projectUpdatedAt
      };

      const chapterSources = {
        preview: chaptersFromDb || [],
        fromDb: chaptersFromDb,
        bootstrap: [],
        projectSummaryCount: projectChapterCount,
        projectTitle: projectTitle || projectStateBookMetadataTitle,
        updatedAt: timestamps
      };

      // Get preliminary result
      const preliminaryState = deriveChaptersState(chapterSources);
      const candidateKey = `${preliminaryState.sourceUsed}:${preliminaryState.chaptersCount}`;

      // Check if we should use the frozen choice or update to newer source
      const latestTimestamp = Object.values(timestamps).filter(Boolean).sort().pop() ?? new Date().toISOString();
      const isNewer =
        !chosenSourceRef.current ||
        (latestTimestamp && chosenSourceRef.current.updatedAt &&
         new Date(latestTimestamp) > new Date(chosenSourceRef.current.updatedAt));

      if (isNewer || chosenSourceRef.current?.key !== candidateKey) {
        chosenSourceRef.current = {
          key: candidateKey,
          updatedAt: latestTimestamp,
        };
        if (process.env.NODE_ENV === 'development') {
        }
      } else if (process.env.NODE_ENV === 'development') {
      }

      const derivedState = preliminaryState;

      // Apply no-downgrade validation if we have existing state
      const currentState = {
        chaptersCount: projectStateChaptersLength ?? 0,
        sourceUsed: 'existing'
      };

      const validation = validateChapterStateChange(currentState, derivedState);

      // Log the transition for debugging
      if (currentState.chaptersCount !== derivedState.chaptersCount && process.env.NODE_ENV === 'development') {
        logChapterStateTransition(currentState, derivedState, validation.allowed, validation.reason);
      }

      // 🔇 Chapters ready check logging (disabled to prevent spam)
      // Uncomment for debugging chapter state issues
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('📚 CHAPTERS READY CHECK (ENHANCED):', {
      //     derivedCount: derivedState.chaptersCount,
      //     sourceUsed: derivedState.sourceUsed
      //   });
      // }

      return {
        isReady: derivedState.chaptersCount > 0,
        chaptersCount: derivedState.chaptersCount,
        sourceUsed: derivedState.sourceUsed,
        confidence: derivedState.confidence,
        projectTitle: derivedState.projectTitle,
        allowOverwrite: derivedState.allowOverwrite && validation.allowed
      };
    } catch (error) {
      console.warn('Error in enhanced chapter state calculation:', error);
      return {
        isReady: false,
        chaptersCount: 0,
        sourceUsed: 'error',
        confidence: 'low' as const,
        allowOverwrite: false
      };
    }
  }, [chaptersFromDb, projectChapterCount, projectUpdatedAt, projectTitle, projectStateChaptersLength, projectStateBookMetadataTitle]);

  // 🚀 SINGLE SOURCE OF TRUTH: Use derived state for all hasChapters decisions
  const canonicalHasChapters = chapterStateResult.chaptersCount > 0;
  const canonicalChaptersCount = chapterStateResult.chaptersCount;
  const isChaptersReady = canonicalHasChapters;

  // ✅ SMART TAB ACTIVATION: Compute project data ready state for book structure tabs
  const isProjectDataReady = useMemo(() => {
    // 🚀 PERFORMANCE FIX: Logic bug - should return false if LOADING (true), not when loaded (false)
    if (!projectState) return false;

    // Check for any meaningful project structure data
    const hasAuthorDetails = projectState.authorDetails?.authorName ||
                           projectState.authorDetails?.authorBio ||
                           (projectState.authorDetails && Object.keys(projectState.authorDetails).length > 0);

    const hasPublishingEssentials = projectState.publishingEssentials &&
                                   Object.keys(projectState.publishingEssentials).length > 0;

    const hasFrontMatter = projectState.frontMatter &&
                          Object.keys(projectState.frontMatter).length > 0;

    const hasBackMatter = projectState.backMatter &&
                         Object.keys(projectState.backMatter).length > 0;

    // For Book Preview, we need at least blueprint data with book metadata
    const hasBookMetadata = projectState.bookMetadata &&
                           (projectState.bookMetadata.title ||
                            projectState.bookMetadata.genreSubject ||
                            Object.keys(projectState.bookMetadata).some(key =>
                              (projectState.bookMetadata as Record<string, any>)[key] && (projectState.bookMetadata as Record<string, any>)[key] !== ''));

    // ✅ SIMPLIFIED: If we have a blueprint, consider project data ready (blueprint contains book metadata)
    const hasBlueprintData = projectState.hasInitialBlueprint || isBlueprintReady;

    const result = hasAuthorDetails || hasPublishingEssentials || hasFrontMatter || hasBackMatter || hasBookMetadata || hasBlueprintData;

    // 🔇 Project data ready check logging (disabled to prevent spam)
    // Uncomment for debugging project data readiness issues
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('🔍 PROJECT DATA READY CHECK:', result);
    // }

    return result;
  }, [projectState, isBlueprintReady]); // 🚀 PERFORMANCE FIX: Removed optimizedLoading from dependencies

  // ---- STUDIO READINESS (story-only, no auth/credits) ----
  const storyDataReady =
    hydrated === true &&
    normalizedCacheData === true &&
    optimizedLoading === false &&
    !!project; // only require 'project' to exist

  // 🔇 Story data readiness logging (disabled to prevent spam)

  // 🔇 Studio gate report (disabled to prevent spam)
  // Uncomment for debugging data loading gates
  // if (process.env.NODE_ENV === 'development') {
  //   const __gateKey = JSON.stringify({
  //     hydrated, normalizedCacheData, optimizedLoading,
  //     projectLoaded: !!project,
  //     blueprintLoaded: !!blueprint,
  //     chaptersCount: chaptersFromDb?.length ?? 0,
  //   });
  //   if ((window as any).__lastGateKey !== __gateKey) {
  //     console.log('🧰 Studio Gate:', __gateKey);
  //     (window as any).__lastGateKey = __gateKey;
  //   }
  // }

  // 🔧 CRITICAL: Cache normalization useEffect MUST be before early return
  useEffect(() => {
    if (!projectId) return;

    // 🔇 Cache normalization logging (disabled to prevent spam)
    const isFirstNormalization = !sessionStorage.getItem(`normalized_${projectId}`);
    if (isFirstNormalization) {
      sessionStorage.setItem(`normalized_${projectId}`, 'true');
    }

    // Synchronously read and normalize caches
    const cacheData = readProjectCachesSync(projectId);

    // Migrate-in-place: add timestamps if missing
    if (cacheData.chapters && cacheData.chapters.length > 0) {
      const now = new Date().toISOString();
      let needsUpdate = false;

      const migratedChapters = cacheData.chapters.map(chapter => {
        if (!chapter.updatedAt && !chapter.chaptersLastUpdate) {
          needsUpdate = true;
          return { ...chapter, updatedAt: now, chaptersLastUpdate: now };
        }
        return chapter;
      });

      if (needsUpdate) {
        // 🔇 Migration logging (disabled)
        try {
          localStorage.setItem(`chapters_${projectId}`, JSON.stringify(migratedChapters));
          cacheData.chapters = migratedChapters;
          cacheData.timestamps.chapters = now;
        } catch (e) {
          console.warn('Failed to migrate chapter timestamps:', e);
        }
      }
    }

    // 🔇 Cache normalization complete (logging disabled)
    setNormalizedCacheData(true); // Set to boolean true for readiness check
    setHydrated(true);
  }, [projectId]);

  // 🔧 CRITICAL: ALL OTHER useEffect HOOKS MUST BE BEFORE EARLY RETURN
  useEffect(() => {
    mountCountRef.current.count++;
    const timeSinceLastMount = Date.now() - mountCountRef.current.lastMount;

    // 🔇 Mount logging (disabled to prevent spam)
    // Uncomment for debugging mount/unmount issues
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('🎯 StoryWorkspacePage MOUNTED! projectId:', projectId);
    //   if (mountCountRef.current.count > 1 && timeSinceLastMount < 5000) {
    //     console.warn('⚠️ Excessive mounting detected:', mountCountRef.current.count);
    //   }
    // }

    mountCountRef.current.lastMount = Date.now();
  }, []); // Only run on mount/unmount

  // Log security warning for numeric IDs being accessed directly
  useEffect(() => {
    if (projectId && !isNaN(Number(projectId))) {
      console.warn('🚨 SECURITY WARNING: User accessing numeric project ID:', projectId);
      console.warn('📌 Blocking API calls until UUID redirect completes');
      console.warn('💡 Use UUID-based URLs: /story-workspace/{uuid} for direct access');
    }
  }, [projectId]);

  // 🚀 STABLE URL SYNC: Only update when URL actually changes
  useEffect(() => {
    if (search !== prevSearchRef.current) {
      const urlTab = new URLSearchParams(search).get("tab") as MainView;
      const targetView = urlTab || 'ideaHub';

      if (targetView !== mainView) {
        // 🔇 Tab navigation logging (disabled to prevent spam)
        setMainView(targetView);
      }
      prevSearchRef.current = search;
    }
  }, [search, mainView]);

  // 🔄 STABLE CHAPTER UPDATE TRACKING: Logging disabled
  useEffect(() => {
    // 🔇 Chapter update logging (disabled to prevent spam)
    if (chapterUpdateTrigger !== prevChapterTriggerRef.current) {
      prevChapterTriggerRef.current = chapterUpdateTrigger;
    }
  }, [chapterUpdateTrigger]);

  // Circuit breaker: detect infinite render loops (placed after all hooks to respect React rules)
  if (renderCountRef.current > 50 && (Date.now() - renderTimestampRef.current) < 3000) {
    renderCountRef.current = 0;
    renderTimestampRef.current = Date.now();
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 font-bold mb-4">Render Loop Detected</div>
          <p className="text-muted-foreground mb-4">The page encountered an infinite render loop.</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-white rounded">
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  if (!storyDataReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Initializing Studio...</p>
        </div>
      </div>
    );
  }

  // ✅ SMART TAB ACTIVATION: Enhanced tooltip function with differentiated requirements
  const getDisabledTooltip = (item: any) => {
    // ✅ BOOK PREVIEW: Requires blueprint + chapters + project data for comprehensive preview
    if (item.view === 'bookPreview') {
      const missing = [];
      if (!isBlueprintReady) missing.push("blueprint");
      if (!isChaptersReady) missing.push("chapters");
      if (!isProjectDataReady) missing.push("project structure data");

      if (missing.length > 0) {
        return `Book Preview requires: ${missing.join(", ")}. Generate content to unlock the preview.`;
      }
    }

    if (item.requiresChapters && !isChaptersReady) {
      return "Chapters will unlock after you create your first chapter outline.";
    }
    if (item.requiresProjectData && !isProjectDataReady) {
      return "This section requires book structure data. It will unlock once you generate a blueprint.";
    }
    if (item.requiresBlueprint && !isBlueprintReady) {
      return "This section requires a blueprint to be generated first.";
    }
    if (item.requiresBlueprint && item.requiresChapters && (!isBlueprintReady || !isChaptersReady)) {
      return "This section unlocks after your first chapter is created.";
    }
    return "";
  };

  // Define navigation items for Writer Studio
  const writerStudioNavItems = [
    { icon: Lightbulb, label: "Project Idea", view: "ideaHub" as MainView, requiresBlueprint: false, requiresChapters: false, requiresProjectData: false },
    { icon: BookOpen, label: "Book Details", view: "bookDetails" as MainView, requiresBlueprint: true, requiresChapters: false, requiresProjectData: false },
    { icon: Users, label: "Characters", view: "characters" as MainView, requiresBlueprint: true, requiresChapters: false, requiresProjectData: false },
    { icon: Layers, label: "World Building", view: "worldBuilding" as MainView, requiresBlueprint: true, requiresChapters: false, requiresProjectData: false },
    { icon: FileText, label: "Chapters Building", view: "chaptersBuilding" as MainView, requiresBlueprint: true, requiresChapters: false, requiresProjectData: false },
    { icon: BookText, label: "Book Structure Studio", view: "bookStructureStudio" as MainView, requiresBlueprint: true, requiresChapters: false, requiresProjectData: true },
    { icon: Package, label: "Publishing Essentials", view: "publishingEssentials" as MainView, requiresBlueprint: true, requiresChapters: false, requiresProjectData: true },
    { icon: Palette, label: "Book Preview", view: "bookPreview" as MainView, requiresBlueprint: true, requiresChapters: true, requiresProjectData: true },
  ];

  // ✅ REMOVED: Old hydration gate replaced by story-data-only gate below

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
      <Header
        onMobileMenuToggle={() => setShowMobileNav(!showMobileNav)}
        mobileMenuOpen={showMobileNav}
        sidebarCollapsed={sidebarCollapsed}
        disableQueries={true} // Disable auth/credits queries in story workspace
      />
      
      {/* Mobile Navigation Overlay */}
      {showMobileNav && (
        <div className="fixed inset-0 bg-black/50 z-50 lg:hidden" onClick={() => setShowMobileNav(false)}>
          <div className="fixed left-0 top-0 h-full w-64 bg-card border-r border-border pt-16" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 space-y-2">
              <Link to="/dashboard" onClick={() => setShowMobileNav(false)}>
                <Button variant="ghost" className="w-full justify-start gap-3 h-12">
                  <Home className="h-4 w-4" />
                  <span>Home</span>
                </Button>
              </Link>
              
              {project && (
                <div className="py-4 border-b border-border">
                  <h2 className="font-semibold text-lg text-foreground truncate" title={safeProjectTitle(project)}>
                    {safeProjectTitle(project)}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {getProjectType(project)} • {getProjectWordCount(project)} words
                  </p>
                </div>
              )}
              
              <div className="space-y-1">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 pt-2">
                  Writer Studio
                </h3>
                {writerStudioNavItems.map((item) => {
                  // ✅ UNIFIED LOGIC: All tabs use same standard requirements logic (Book Preview now has same requirements as Chapters Building)
                  const isDisabled = (item.requiresBlueprint && !isBlueprintReady) || (item.requiresChapters && !isChaptersReady) || (item.requiresProjectData && !isProjectDataReady);
                  const tooltipMessage = isDisabled ? getDisabledTooltip(item) : "";
                  
                  return (
                    <Tooltip key={item.view}>
                      <TooltipTrigger asChild>
                        <Link to={!isDisabled ? `/story-workspace/${effectiveProjectId}?tab=${item.view}` : '#'} onClick={() => setShowMobileNav(false)}>
                          <Button 
                            variant={mainView === item.view ? "secondary" : "ghost"} 
                            className={cn(
                              "w-full justify-start gap-3 h-12",
                              isDisabled && "text-muted-foreground/50 cursor-not-allowed hover:bg-transparent"
                            )}
                            disabled={isDisabled}
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.label}</span>
                          </Button>
                        </Link>
                      </TooltipTrigger>
                      {isDisabled && (
                        <TooltipContent>
                          <p>{tooltipMessage}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block w-64 bg-card border-r border-border min-h-screen pt-16">
          <div className="p-4 space-y-2">
            <Link to="/dashboard">
              <Button variant="ghost" className="w-full justify-start gap-3 h-10">
                <Home className="h-4 w-4" />
                <span>Home</span>
              </Button>
            </Link>
            {/* Debug buttons - only in development */}
            {process.env.NODE_ENV === 'development' && project && (
              <div className="py-2 border-t border-border">
                <button
                  onClick={() => {
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground mb-1 block w-full text-left"
                >
                  🐛 Debug: Print State
                </button>
                <button
                  onClick={() => {
                    if (project?.uuid && window.confirm('Clear cache for this project? This will force a refresh of all data.')) {
                      cacheManager.clearProjectCache(project.uuid);
                      window.location.reload();
                    }
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground block w-full text-left"
                >
                  🗑️ Clear Project Cache
                </button>
              </div>
            )}
            
            {project && (
              <div className="py-4 border-b border-border">
                <h2 className="font-semibold text-lg text-foreground truncate" title={safeProjectTitle(project)}>
                  {safeProjectTitle(project)}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {getProjectType(project)} • {getProjectWordCount(project)} words
                </p>
              </div>
            )}
            
            <div className="space-y-1">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 pt-2">
                Writer Studio
              </h3>
              {writerStudioNavItems.map((item) => {
                // ✅ UNIFIED LOGIC: All tabs use same standard requirements logic (Book Preview now has same requirements as Chapters Building)
                const isDisabled = (item.requiresBlueprint && !isBlueprintReady) || (item.requiresChapters && !isChaptersReady) || (item.requiresProjectData && !isProjectDataReady);
                const tooltipMessage = isDisabled ? getDisabledTooltip(item) : "";
                
                return (
                  <Tooltip key={item.view}>
                    <TooltipTrigger asChild>
                      <Link to={!isDisabled ? `/story-workspace/${effectiveProjectId}?tab=${item.view}` : '#'}>
                        <Button 
                          variant={mainView === item.view ? "secondary" : "ghost"} 
                          className={cn(
                            "w-full justify-start gap-3 h-10",
                            isDisabled && "text-muted-foreground/50 cursor-not-allowed hover:bg-transparent"
                          )}
                          disabled={isDisabled}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    {isDisabled && (
                      <TooltipContent>
                        <p>{tooltipMessage}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                )
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 lg:ml-0">
          {/* Show loading indicator when story data is not ready */}
          {!storyDataReady || !projectState ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  {!hydrated ? 'Initializing cache...' :
                   !normalizedCacheData ? 'Normalizing project data...' :
                   optimizedLoading ? 'Loading story data...' :
                   !project ? 'Loading project...' :
                   'Initializing Studio...'}
                </p>
              </div>
            </div>
          ) : (
            <WriteStudio
              showGlobalSidebar={false}
              projectId={effectiveProjectId} // Always use UUID when available
              activeView={mainView}
              onViewChange={(view) => navigateToTab(view, false)}
              project={project as Project | null}
              projectState={projectState}
              setProjectState={setProjectState}
              optimizedData={optimizedData}
            />
          )}
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}